import React, { useRef, useState, useCallback, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';

const THRESHOLD = 80; // px to trigger refresh
const MAX_PULL = 120; // max pull distance

const PullToRefresh = ({ onRefresh, children, isRefreshing = false }) => {
  const containerRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const pullDistanceRef = useRef(0); // sync ref to avoid stale closure in touchEnd
  const [phase, setPhase] = useState('idle'); // idle | pulling | refreshing
  const startY = useRef(0);
  const currentY = useRef(0);
  const pulling = useRef(false);

  // Reset on refresh complete
  useEffect(() => {
    if (!isRefreshing && phase === 'refreshing') {
      setPhase('idle');
      setPullDistance(0);
    }
  }, [isRefreshing, phase]);

  const handleTouchStart = useCallback((e) => {
    // Solo activar el pull-to-refresh cuando la ventana está arriba del todo.
    // El contenedor tiene overflow-hidden y no hace scroll propio: el scroll
    // real es el de la ventana, así que medimos window.scrollY (antes se usaba
    // containerRef.scrollTop, que siempre era 0).
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    if (scrollTop <= 0) {
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!pulling.current || phase === 'refreshing') return;

    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    if (diff > 0) {
      // Resistive pull (feels more natural)
      const distance = Math.min(Math.sqrt(diff * 5), MAX_PULL);
      pullDistanceRef.current = distance;
      setPullDistance(distance);
      setPhase('pulling');
    }
  }, [phase]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    const currentPull = pullDistanceRef.current;

    if (currentPull >= THRESHOLD && phase === 'pulling') {
      setPhase('refreshing');
      try {
        await onRefresh();
      } catch (e) {
        console.error('PullToRefresh error:', e);
      }
    } else {
      setPhase('idle');
      setPullDistance(0);
      pullDistanceRef.current = 0;
    }
  }, [phase, onRefresh]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const showIndicator = phase === 'pulling' || phase === 'refreshing';

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'manipulation', overscrollBehaviorY: 'contain' }}
    >
      {/* Pull indicator */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center transition-all duration-200 z-10"
        style={{
          top: showIndicator ? 0 : -60,
          height: Math.max(pullDistance, 0),
          opacity: Math.min(progress * 1.5, 1),
        }}
      >
        <div className={`flex flex-col items-center gap-1 ${phase === 'refreshing' ? 'animate-pulse' : ''}`}>
          <RefreshCw
            className={`w-6 h-6 text-orange-500 ${
              phase === 'refreshing' ? 'animate-spin' : ''
            }`}
            style={{
              transform: phase === 'pulling' ? `rotate(${progress * 360}deg)` : undefined,
              transition: phase === 'pulling' ? 'none' : undefined,
            }}
          />
          <span className="text-xs font-medium text-orange-600">
            {phase === 'refreshing'
              ? 'Actualizando...'
              : progress >= 1
              ? 'Suelta para actualizar'
              : 'Tira para refrescar'}
          </span>
        </div>
      </div>

      {/* Content wrapper with pull offset */}
      <div
        className="transition-transform duration-200 ease-out"
        style={{
          transform: showIndicator ? `translateY(${Math.min(pullDistance, 60)}px)` : 'translateY(0)',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
